<template>
  <div>
    <!-- Platform infos -->
    <b-row>
      <b-col>
        <b-card
          v-if="loading"
          class="text-center"
        >
          <b-spinner label="Loading..." />
        </b-card>
        <b-card v-else>

          <b-row>

            <!-- User Info: Left col -->
            <b-col
              cols="21"
              xl="6"
              class="d-flex justify-content-between flex-column"
            >
              <!-- User Avatar & Action Buttons -->
              <div class="d-flex justify-content-start">
                <b-avatar
                  :src="platform.img_logo_after_auth"
                  :text="platform.name"
                  :variant="`light-primary`"
                  size="104px"
                  rounded
                />
                <div class="d-flex flex-column ml-1 text-truncate">
                  <div class="mb-1">
                    <h4 class="mb-0">
                      {{ platform.name }}
                    </h4>
                    <b-link
                      class="text-truncate d-block"
                      :href="`https://${platform.subdomain}.ccup.io`"
                      target="_blank"
                      rel="noopener noreferrer"
                    >{{ `${platform.subdomain}.ccup.io` }}</b-link>
                  </div>
                  <div class="d-flex flex-wrap">
                    <b-button
                      :to="{ name: 'platforms-edit', params: { id: platform.uuid } }"
                      variant="primary"
                    >
                      Modifier
                    </b-button>
                    <b-button
                      variant="outline-primary"
                      class="ml-0 ml-sm-1"
                      :disabled="brandAnalysisLoading || !platform.client_website_url"
                      @click="analyzeBrand(false)"
                    >
                      <b-spinner
                        v-show="brandAnalysisLoading"
                        small
                      />
                      Analyser le site
                    </b-button>
                    <b-button
                      v-b-modal="`modal-delete-platform-${platform.uuid}`"
                      variant="outline-danger"
                      class="ml-0 ml-sm-1"
                      :disabled="deleteLoading"
                    >
                      Supprimer
                    </b-button>
                    <b-modal
                      :id="`modal-delete-platform-${platform.uuid}`"
                      :ref="`modal-delete-platform-${platform.uuid}`"
                      ok-variant="danger"
                      ok-title="Supprimer"
                      modal-class="modal-danger"
                      centered
                      title="Suppression de la plateforme"
                    >
                      <b-card-text>
                        Souhaitez-vous vraiment supprimer cette plateforme ?<br>
                        Ces éléments seront supprimés :<br>
                        -Pronostics (sur les matchs et les questions)<br>
                        -Équipe<br>
                        -Prix<br>
                        -Messages<br>
                        -Chatbot (et ses messages)<br>
                        -Mails autorisés<br>
                        -Invitations<br>
                        -Sponsors<br>
                        -Notifications<br>
                        ✔Les utilisateurs seront encore accessibles
                        <p class="text-danger font-weight-bold">
                          (Cette action est réversible)
                        </p>
                      </b-card-text>
                      <template #modal-footer>
                        <b-button
                          variant="secondary"
                          @click="hideModal()"
                        >
                          annuler
                        </b-button>
                        <b-button
                          variant="danger"
                          :disabled="deleteLoading"
                          @click="deletePlatform()"
                        >
                          <b-spinner
                            v-show="deleteLoading"
                            small
                          />
                          supprimer
                        </b-button>
                      </template>
                    </b-modal>
                  </div>
                </div>
              </div>

              <!-- User Stats -->
              <div class="d-flex align-items-center mt-2">
                <div class="d-flex align-items-center mr-2">
                  <b-avatar
                    variant="light-primary"
                    rounded
                  >
                    <feather-icon
                      icon="DollarSignIcon"
                      size="18"
                    />
                  </b-avatar>
                  <div class="ml-1">
                    <h5 class="mb-0">
                      {{ platform.slot * 2 }}€ ht
                    </h5>
                    <small>Prix 2€/places reservées</small>
                  </div>
                </div>

                <div class="d-flex align-items-center">
                  <b-avatar
                    variant="light-success"
                    rounded
                  >
                    <feather-icon
                      icon="CalendarIcon"
                      size="18"
                    />
                  </b-avatar>
                  <div class="ml-1">
                    <h5 class="mb-0">
                      {{ moment(platform.created_at).format("LLL") }}
                    </h5>
                    <small>Date de création</small>
                  </div>
                </div>
              </div>
            </b-col>

            <!-- Right Col: Table -->
            <b-col
              cols="12"
              xl="6"
              class="text-truncate"
            >
              <table class="mt-2 mt-xl-0 w-100">
                <tr>
                  <th class="pb-50">
                    <feather-icon
                      icon="UserIcon"
                      class="mr-75"
                    />
                    <span class="font-weight-bold">Nom</span>
                  </th>
                  <td class="pb-50">
                    {{ platform.name }}
                  </td>
                </tr>
                <tr>
                  <th class="pb-50">
                    <feather-icon
                      icon="Link2Icon"
                      class="mr-75"
                    />
                    <span class="font-weight-bold">Sous domaine</span>
                  </th>
                  <td class="pb-50 text-capitalize">
                    {{ platform.subdomain }}
                  </td>
                </tr>
                <tr>
                  <th class="pb-50">
                    <feather-icon
                      icon="ExternalLinkIcon"
                      class="mr-75"
                    />
                    <span class="font-weight-bold">Site officiel</span>
                  </th>
                  <td class="pb-50">
                    <b-link
                      v-if="platform.client_website_url"
                      :href="platform.client_website_url"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {{ platform.client_website_url }}
                    </b-link>
                    <span v-else>-</span>
                  </td>
                </tr>
                <tr>
                  <th class="pb-50">
                    <feather-icon
                      icon="UsersIcon"
                      class="mr-75"
                    />
                    <span class="font-weight-bold">Slot</span>
                  </th>
                  <td class="pb-50 text-capitalize">
                    {{ platform.slot }}
                  </td>
                </tr>
                <tr>
                  <th class="pb-50">
                    <feather-icon
                      icon="MessageSquareIcon"
                      class="mr-75"
                    />
                    <span class="font-weight-bold">Messagerie</span>
                  </th>
                  <td class="pb-50 text-capitalize">
                    {{ platform.is_messaging_enable ? 'activée': 'désactivée' }}
                  </td>
                </tr>
                <tr>
                  <th class="pb-50">
                    <feather-icon
                      icon="DribbbleIcon"
                      class="mr-75"
                    />
                    <span class="font-weight-bold">Competitions</span>
                  </th>
                  <td
                    v-if="platform.competitions"
                    class="pb-50"
                  >
                    <b-link
                      v-for="competition in platform.competitions"
                      :key="competition.uuid"
                      class="mr-1"
                      :to="{ name: 'competitions-view', params: { id: competition.uuid } }"
                    >{{ `${competition.name}` }}</b-link>
                  </td>
                </tr>
                <tr>
                  <th class="pb-50">
                    <feather-icon
                      icon="Share2Icon"
                      class="mr-75"
                    />
                    <span class="font-weight-bold">Token invitation</span>
                  </th>
                  <td v-if="platform.invitation">
                    {{ platform.invitation.token }}
                  </td>
                </tr>
                <tr>
                  <th>
                    <feather-icon
                      icon="FlagIcon"
                      class="mr-75"
                    />
                    <span class="font-weight-bold">Languages</span>
                  </th>
                  <td v-if="platform.languages">
                    <b-badge
                      v-for="(language,index) in platform.languages"
                      :key="index"
                      class="mr-1"
                      variant="light-primary"
                    >
                      {{ language.language_code }}
                    </b-badge>
                  </td>
                </tr>
              </table>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>
    <b-row v-if="brandAnalysis || brandAnalysisError || brandAnalysisLoading">
      <b-col>
        <b-card>
          <template #header>
            <div class="brand-card-header">
              <div class="brand-card-title">
                <h4 class="mb-25">
                  Charte graphique detectee
                </h4>
                <small class="text-muted">{{ brandSourceLabel }}</small>
              </div>
              <div class="brand-card-actions">
                <b-button
                  variant="outline-warning"
                  class="mr-0 mr-sm-1 mb-50 mb-sm-0"
                  :disabled="brandAnalysisLoading || brandApplyLoading || !platform.client_website_url"
                  @click="analyzeBrand(true)"
                >
                  <b-spinner
                    v-show="brandAnalysisLoading"
                    small
                  />
                  Reanalyser
                </b-button>
              </div>
            </div>
          </template>
          <div
            v-if="brandAnalysisLoading && !brandAnalysis"
            class="brand-loading"
          >
            <b-spinner class="mr-75" />
            <span>Analyse du site client en cours...</span>
          </div>
          <b-alert
            v-else-if="brandAnalysisError"
            show
            variant="danger"
            class="mb-0"
          >
            {{ brandAnalysisError }}
          </b-alert>
          <template v-else>
            <div class="brand-summary-band">
              <div class="brand-summary-main">
                <div class="brand-summary-title">
                  {{ brandAnalysis.site_name || brandAnalysis.resolved_url }}
                </div>
                <div
                  v-if="brandAnalysis.resolved_url"
                  class="brand-summary-meta"
                >
                  <b-link
                    :href="brandAnalysis.resolved_url"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ shortUrl(brandAnalysis.resolved_url) }}
                  </b-link>
                  <span v-if="brandAnalyzedAtLabel">Analyse du {{ brandAnalyzedAtLabel }}</span>
                </div>
              </div>
              <div class="brand-metrics">
                <div class="brand-metric">
                  <span class="brand-metric-value">{{ brandLogoCount }}</span>
                  <span class="brand-metric-label">logos</span>
                </div>
                <div class="brand-metric">
                  <span class="brand-metric-value">{{ brandColorCount }}</span>
                  <span class="brand-metric-label">couleurs</span>
                </div>
                <div class="brand-metric">
                  <span class="brand-metric-value">{{ brandFontCount }}</span>
                  <span class="brand-metric-label">fonts</span>
                </div>
              </div>
            </div>
            <b-alert
              v-if="brandWarningCount"
              show
              variant="warning"
              class="mb-1"
            >
              <div class="font-weight-bold mb-50">
                {{ brandWarningCount }} element(s) ignore(s) pendant l'analyse.
              </div>
              <div
                v-for="warning in brandWarningsPreview"
                :key="warning"
              >
                {{ warning }}
              </div>
            </b-alert>
            <div class="brand-discovery-grid">
              <section class="brand-panel brand-panel-logos">
                <div class="brand-panel-header">
                  <h5 class="mb-0">
                    Logos
                  </h5>
                  <b-badge variant="light-primary">
                    {{ brandLogoCount }}
                  </b-badge>
                </div>
                <div
                  v-if="brandAnalysis.logos && brandAnalysis.logos.length"
                  class="brand-logo-list"
                >
                  <div
                    v-for="logo in brandAnalysis.logos"
                    :key="logo.url"
                    class="brand-logo-candidate"
                  >
                    <b-img
                      :src="logo.url"
                      fluid
                      :alt="logo.label || logo.type"
                      referrerpolicy="no-referrer"
                    />
                    <div class="brand-logo-meta">
                      <b-badge
                        variant="light-primary"
                        class="mr-50"
                      >
                        {{ logo.type }}
                      </b-badge>
                      <span class="brand-logo-label">{{ logo.label || shortUrl(logo.url) }}</span>
                    </div>
                    <b-link
                      :href="logo.url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="brand-logo-link"
                    >
                      {{ shortUrl(logo.url) }}
                    </b-link>
                  </div>
                </div>
                <b-card-text
                  v-else
                  class="text-muted mb-0"
                >
                  Aucun logo detecte.
                </b-card-text>
              </section>
              <section class="brand-panel">
                <div class="brand-panel-header">
                  <h5 class="mb-0">
                    Couleurs
                  </h5>
                  <b-badge variant="light-primary">
                    {{ brandColorCount }}
                  </b-badge>
                </div>
                <div
                  v-if="detectedBrandColors.length"
                  class="brand-color-grid"
                >
                  <div
                    v-for="color in detectedBrandColors"
                    :key="color"
                    class="brand-color-token"
                  >
                    <span
                      class="brand-color-swatch"
                      :style="{ backgroundColor: color }"
                    />
                    <span>{{ color }}</span>
                  </div>
                </div>
                <b-card-text
                  v-else
                  class="text-muted mb-0"
                >
                  Aucune couleur detectee.
                </b-card-text>
                <div
                  v-if="brandCssVariables.length"
                  class="brand-css-vars"
                >
                  <h6>Variables CSS</h6>
                  <div
                    v-for="color in brandCssVariables"
                    :key="`${color.name}-${color.value}`"
                    class="brand-css-var"
                  >
                    <span
                      class="brand-color-swatch brand-color-swatch-sm"
                      :style="{ backgroundColor: color.value }"
                    />
                    <span class="brand-css-var-name">{{ color.name }}</span>
                    <span>{{ color.value }}</span>
                  </div>
                </div>
              </section>
              <section class="brand-panel">
                <div class="brand-panel-header">
                  <h5 class="mb-0">
                    Fonts
                  </h5>
                  <b-badge variant="light-secondary">
                    {{ brandFontCount }}
                  </b-badge>
                </div>
                <div
                  v-if="brandAnalysis.fonts && brandAnalysis.fonts.length"
                  class="brand-font-list"
                >
                  <b-badge
                    v-for="font in brandAnalysis.fonts"
                    :key="font"
                    variant="light-secondary"
                    class="mr-50 mb-50"
                  >
                    {{ font }}
                  </b-badge>
                </div>
                <b-card-text
                  v-else
                  class="text-muted mb-0"
                >
                  Aucune font detectee.
                </b-card-text>
              </section>
            </div>
            <div class="brand-apply-section">
              <div class="d-flex flex-wrap align-items-center justify-content-between mb-1">
                <div>
                  <h5 class="mb-25">
                    Personnalisation a appliquer
                  </h5>
                  <div class="text-muted">
                    {{ selectedBrandFieldCount }} champ(s) selectionne(s)
                  </div>
                </div>
                <div class="brand-card-actions">
                  <b-button
                    variant="outline-secondary"
                    class="mr-0 mr-sm-1 mb-50 mb-sm-0"
                    :disabled="brandApplyLoading || !hasBrandApplySelection"
                    @click="resetBrandSelections"
                  >
                    Reinitialiser
                  </b-button>
                  <b-button
                    variant="primary"
                    :disabled="brandApplyLoading || !hasBrandApplySelection"
                    @click="showBrandApplyConfirmation"
                  >
                    <b-spinner
                      v-show="brandApplyLoading"
                      small
                    />
                    Appliquer
                  </b-button>
                </div>
              </div>
              <b-card-text
                v-if="!hasDetectedBrandOptions"
                class="text-muted"
              >
                Aucun logo ou couleur applicable.
              </b-card-text>
              <template v-else>
                <b-row>
                  <b-col
                    v-for="field in brandImageFields"
                    :key="field.key"
                    cols="12"
                    md="4"
                  >
                    <b-form-group :label="field.label">
                      <v-select
                        v-model="brandImageSelection[field.key]"
                        :dir="'ltr'"
                        :options="brandLogoOptions"
                        :reduce="option => option.value"
                        label="text"
                        :clearable="false"
                        class="brand-select"
                      >
                        <template #option="option">
                          <div class="brand-select-option">
                            <span class="brand-select-logo-preview">
                              <img
                                v-if="option.value"
                                :src="option.value"
                                :alt="option.typeLabel || option.text"
                                referrerpolicy="no-referrer"
                              >
                            </span>
                            <span class="brand-select-option-text">
                              <span class="brand-select-option-title">{{ option.text }}</span>
                              <small
                                v-if="option.typeLabel"
                                class="text-muted"
                              >{{ option.typeLabel }}</small>
                            </span>
                          </div>
                        </template>
                        <template #selected-option="option">
                          <div class="brand-select-option brand-select-option-selected">
                            <span class="brand-select-logo-preview">
                              <img
                                v-if="option.value"
                                :src="option.value"
                                :alt="option.typeLabel || option.text"
                                referrerpolicy="no-referrer"
                              >
                            </span>
                            <span class="brand-select-option-title">{{ option.text }}</span>
                          </div>
                        </template>
                      </v-select>
                      <div
                        v-if="brandImageSelection[field.key]"
                        class="brand-selected-image"
                      >
                        <span class="brand-select-logo-preview">
                          <img
                            :src="brandImageSelection[field.key]"
                            :alt="field.label"
                            referrerpolicy="no-referrer"
                          >
                        </span>
                        <small class="text-muted">{{ shortUrl(brandImageSelection[field.key]) }}</small>
                      </div>
                    </b-form-group>
                  </b-col>
                </b-row>
                <div
                  v-for="group in groupedBrandColorFields"
                  :key="group.key"
                  class="brand-color-group"
                >
                  <h6>{{ group.label }}</h6>
                  <b-row>
                    <b-col
                      v-for="field in group.fields"
                      :key="field.key"
                      cols="12"
                      md="4"
                      xl="3"
                    >
                      <b-form-group :label="field.label">
                        <v-select
                          v-model="brandColorSelection[field.key]"
                          :dir="'ltr'"
                          :options="brandColorOptions"
                          :reduce="option => option.value"
                          label="text"
                          :clearable="false"
                          class="brand-select"
                        >
                          <template #option="option">
                            <div class="brand-select-option">
                              <span
                                class="brand-color-swatch brand-color-swatch-option"
                                :class="{ 'brand-color-swatch-empty': !option.value }"
                                :style="option.value ? { backgroundColor: option.value } : {}"
                              />
                              <span class="brand-select-option-title">{{ option.text }}</span>
                            </div>
                          </template>
                          <template #selected-option="option">
                            <div class="brand-select-option brand-select-option-selected">
                              <span
                                class="brand-color-swatch brand-color-swatch-option"
                                :class="{ 'brand-color-swatch-empty': !option.value }"
                                :style="option.value ? { backgroundColor: option.value } : {}"
                              />
                              <span class="brand-select-option-title">{{ option.text }}</span>
                            </div>
                          </template>
                        </v-select>
                        <div
                          v-if="brandColorSelection[field.key]"
                          class="brand-selected-color"
                        >
                          <span
                            class="brand-color-swatch brand-color-swatch-sm"
                            :style="{ backgroundColor: brandColorSelection[field.key] }"
                          />
                          <span>{{ brandColorSelection[field.key] }}</span>
                        </div>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </div>
              </template>
            </div>
          </template>
        </b-card>
      </b-col>
    </b-row>
    <b-modal
      ref="brand-apply-modal"
      title="Ecraser la personnalisation"
      modal-class="modal-warning"
      centered
      hide-footer
    >
      <b-card-text class="font-weight-bold text-warning mb-1">
        Attention, les elements selectionnes vont ecraser la personnalisation actuelle de la plateforme.
      </b-card-text>
      <b-card-text>
        Ce panneau affiche la charte detectee sur le site officiel client. Il ne represente pas forcement la personnalisation actuellement visible sur la plateforme en production.
      </b-card-text>
      <b-card-text class="mb-1">
        Seuls les champs ci-dessous seront modifies. Les champs sur "Ne pas modifier" ne changeront pas.
      </b-card-text>
      <div
        v-if="selectedBrandImageFields.length"
        class="mb-1"
      >
        <h6>Images remplacees</h6>
        <div
          v-for="field in selectedBrandImageFields"
          :key="field.key"
          class="brand-confirm-item"
        >
          <span class="brand-select-logo-preview">
            <img
              :src="field.value"
              :alt="field.label"
              referrerpolicy="no-referrer"
            >
          </span>
          <span class="brand-select-option-text">
            <span class="brand-select-option-title">{{ field.label }}</span>
            <small class="text-muted">{{ shortUrl(field.value) }}</small>
          </span>
        </div>
      </div>
      <div
        v-if="selectedBrandColorFields.length"
        class="mb-1"
      >
        <h6>Couleurs remplacees</h6>
        <div
          v-for="field in selectedBrandColorFields"
          :key="field.key"
          class="brand-confirm-item"
        >
          <span
            class="brand-color-swatch brand-color-swatch-option"
            :style="{ backgroundColor: field.value }"
          />
          <span>{{ field.label }} - {{ field.value }}</span>
        </div>
      </div>
      <div class="d-flex justify-content-end mt-2">
        <b-button
          variant="outline-secondary"
          class="mr-1"
          :disabled="brandApplyLoading"
          @click="hideBrandApplyConfirmation"
        >
          Annuler
        </b-button>
        <b-button
          variant="warning"
          :disabled="brandApplyLoading || !hasBrandApplySelection"
          @click="applyBrandAnalysis"
        >
          <b-spinner
            v-show="brandApplyLoading"
            small
          />
          Ecraser la personnalisation
        </b-button>
      </div>
    </b-modal>
    <b-row>
      <b-col
        xl="3"
        md="4"
        sm="6"
      >
        <b-card
          v-if="loading"
          class="text-center"
        >
          <b-spinner label="Loading..." />
        </b-card>
        <statistic-card-horizontal
          v-else
          color="success"
          icon="UsersIcon"
          :statistic="`${platform.users_count}/${platform.slot}`"
          statistic-title="Joueurs"
        />
      </b-col>
      <b-col
        xl="3"
        md="4"
        sm="6"
      >
        <b-card
          v-if="loading"
          class="text-center"
        >
          <b-spinner label="Loading..." />
        </b-card>
        <statistic-card-horizontal
          v-else
          color="danger"
          icon="TrendingUpIcon"
          :statistic="`${platform.participation_rate || 0}%`"
          statistic-title="Participation"
        />
      </b-col>
      <b-col
        xl="3"
        md="4"
        sm="6"
      >
        <b-card
          v-if="loading"
          class="text-center"
        >
          <b-spinner label="Loading..." />
        </b-card>
        <statistic-card-horizontal
          v-else
          color="warning"
          icon="ZapIcon"
          :statistic="platform.forecasts_count + platform.forecasts_quiz_count"
          statistic-title="Pronostics"
        />
        <b-card
          v-if="loading"
          class="text-center"
        >
          <b-spinner label="Loading..." />
        </b-card>
        <b-card v-else>
          <b-card-text class="mb-1">
            Taux de bons pronotics
          </b-card-text>
          <b-progress
            variant="warning"
            max="100"
          >
            <b-progress-bar
              :value="rateGoodForecast"
              :label="`${rateGoodForecast}%`"
              variant="warning"
            />
          </b-progress>
        </b-card>
      </b-col>
      <b-col
        xl="3"
        md="4"
        sm="6"
      >
        <b-card
          v-if="loading"
          class="text-center"
        >
          <b-spinner label="Loading..." />
        </b-card>
        <statistic-card-horizontal
          v-else
          color="info"
          icon="MessageSquareIcon"
          :statistic="platform.messages_count"
          statistic-title="Messages"
        />
        <b-card
          v-if="loading"
          class="text-center"
        >
          <b-spinner label="Loading..." />
        </b-card>
        <statistic-card-horizontal
          v-else
          color="info"
          icon="MessageSquareIcon"
          :statistic="platform.messages_today_count"
          statistic-title="Messages aujourd'hui"
        />
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-card
          v-if="loading"
          class="text-center"
        >
          <b-spinner label="Loading..." />
        </b-card>
        <template v-else>
          <PlatformViewPrizes />
          <PlatformViewTeams />
        </template>
      </b-col>
      <b-col>
        <b-card
          v-if="loading"
          class="text-center"
        >
          <b-spinner label="Loading..." />
        </b-card>
        <template v-else>
          <PlatformViewChatbot />
          <PlatformViewAllowedMails />
          <PlatformViewSponsors />
        </template>
      </b-col>
    </b-row>
    <h3 class="text-center mb-3 mt-1">
      Utilisateurs
    </h3>
    <b-card no-body>
      <users-list-filters
        :role-filter.sync="userRoleFilter"
        :trashed-filter.sync="userTrashedFilter"
        :role-options="userRoleOptions"
        :trashed-options="userTrashedOptions"
      />
      <users-list
        :api-url="apiUrl"
        :role-filter="userRoleFilter"
        :platform-filter="userPlatformFilter"
        :trashed-filter="userTrashedFilter"
        :table-columns="userTableColumns"
      />
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BLink, BRow, BCol, BAvatar, BButton, BSpinner, BProgress, BProgressBar, BCardText, BModal, BBadge, BImg, BFormGroup,
  BAlert,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import StatisticCardHorizontal from '@core/components/statistics-cards/StatisticCardHorizontal.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import PlatformViewPrizes from '@/views/platforms/platforms-view/PlatformViewPrizes.vue'
import PlatformViewChatbot from '@/views/platforms/platforms-view/PlatformViewChatbot.vue'
import PlatformViewTeams from '@/views/platforms/platforms-view/PlatformViewTeams.vue'
import PlatformViewAllowedMails from '@/views/platforms/platforms-view/PlatformViewAllowedMails.vue'
import PlatformViewSponsors from '@/views/platforms/platforms-view/PlatformViewSponsors.vue'
import UsersListFilters from '@/components/lists/users/UsersFilter.vue'
import UsersList from '@/components/lists/users/UsersList.vue'
import { mapGetters } from 'vuex'
import axiosIns from '@/libs/axios'

export default {
  name: 'PlatformsView',
  components: {
    BRow,
    BCol,
    BCard,
    BLink,
    BAvatar,
    BButton,
    BSpinner,
    PlatformViewPrizes,
    PlatformViewChatbot,
    PlatformViewTeams,
    PlatformViewAllowedMails,
    PlatformViewSponsors,
    StatisticCardHorizontal,
    BProgress,
    BCardText,
    BModal,
    BProgressBar,
    UsersListFilters,
    UsersList,
    BBadge,
    BImg,
    BFormGroup,
    BAlert,
    vSelect,
  },
  data() {
    return {
      loading: true,
      deleteLoading: false,
      brandAnalysisLoading: false,
      brandApplyLoading: false,
      brandAnalysis: null,
      brandAnalysisError: null,
      brandAnalysisCached: false,
      brandImageSelection: {
        favicon: '',
        img_logo_before_auth: '',
        img_logo_after_auth: '',
      },
      brandColorSelection: {
        color_background_before_auth: '',
        color_text_before_auth: '',
        color_button_before_auth: '',
        color_navigation_link_before_auth: '',
        color_background_after_auth: '',
        color_navigation_fixture: '',
        color_card_fixture: '',
        color_arrow_fixture: '',
        color_notification_fixture: '',
        color_navigation_platform: '',
        color_navigation_active_platform: '',
        color_navigation_background_platform: '',
        color_navigation_notification: '',
        color_badges_rank: '',
      },
      brandImageFields: [
        { key: 'favicon', label: 'Favicon' },
        { key: 'img_logo_before_auth', label: 'Logo avant connexion' },
        { key: 'img_logo_after_auth', label: 'Logo apres connexion' },
      ],
      brandColorFieldGroups: [
        { key: 'before_auth', label: 'Avant connexion' },
        { key: 'after_auth', label: 'Apres connexion' },
        { key: 'fixtures', label: 'Matchs' },
        { key: 'navigation', label: 'Navigation' },
        { key: 'ranking', label: 'Classement' },
      ],
      brandColorFields: [
        { key: 'color_background_before_auth', label: 'Fond', group: 'before_auth' },
        { key: 'color_text_before_auth', label: 'Texte', group: 'before_auth' },
        { key: 'color_button_before_auth', label: 'Bouton', group: 'before_auth' },
        { key: 'color_navigation_link_before_auth', label: 'Liens', group: 'before_auth' },
        { key: 'color_background_after_auth', label: 'Fond', group: 'after_auth' },
        { key: 'color_navigation_fixture', label: 'Navigation', group: 'fixtures' },
        { key: 'color_card_fixture', label: 'Cartes', group: 'fixtures' },
        { key: 'color_arrow_fixture', label: 'Fleches', group: 'fixtures' },
        { key: 'color_notification_fixture', label: 'Notifications', group: 'fixtures' },
        { key: 'color_navigation_platform', label: 'Texte navigation', group: 'navigation' },
        { key: 'color_navigation_active_platform', label: 'Navigation active', group: 'navigation' },
        { key: 'color_navigation_background_platform', label: 'Fond navigation', group: 'navigation' },
        { key: 'color_navigation_notification', label: 'Notification navigation', group: 'navigation' },
        { key: 'color_badges_rank', label: 'Badges', group: 'ranking' },
      ],
      userPlatformFilter: null,
      userRoleFilter: null,
      userPlatformOptions: [],
      userRoleOptions: [
        { label: 'superadmins', value: 'superAdmin' },
        { label: 'admins', value: 'admin' },
        { label: 'joueurs', value: 'player' },
      ],
      userTrashedFilter: false,
      userTrashedOptions: [
        { label: 'Oui', value: true },
        { label: 'Non', value: false },
      ],
      userTableColumns: [
        {
          key: 'name',
          label: 'Utilisateur',
          sortable: true,
        },
        {
          key: 'email',
          label: 'Email',
          sortable: false,
        },
        {
          key: 'role',
          label: 'Role',
          sortable: false,
        },
        {
          key: 'notification',
          label: 'Notification',
          sortable: false,
        },
        {
          key: 'email_verified',
          label: 'Mail vérifié',
          sortable: false,
        },
        {
          key: 'created_at',
          label: 'Date de d\'inscription',
          sortable: true,
        },
        { key: 'actions' },
      ],
      apiUrl: 'users',
    }
  },
  computed: {
    ...mapGetters({
      platform: 'platforms/platform',
    }),
    brandLogoOptions() {
      const options = [{ value: '', text: 'Ne pas modifier', typeLabel: '' }]
      if (!this.brandAnalysis || !this.brandAnalysis.logos) {
        return options
      }

      this.brandAnalysis.logos.forEach(logo => {
        if (!logo.url) {
          return
        }

        options.push({
          value: logo.url,
          text: `${logo.type || 'logo'} - ${this.shortUrl(logo.url)}`,
          typeLabel: logo.label || logo.type || '',
        })
      })

      return options
    },
    brandColorOptions() {
      const options = [{ value: '', text: 'Ne pas modifier' }]
      this.detectedBrandColors.forEach(color => {
        options.push({
          value: color,
          text: color,
        })
      })
      return options
    },
    brandSourceLabel() {
      if (!this.brandAnalysis) {
        return 'Aucune analyse chargee'
      }

      if (this.brandAnalysisCached) {
        return 'Analyse stockee en base'
      }

      return 'Analyse recente du site officiel'
    },
    brandAnalyzedAtLabel() {
      if (!this.brandAnalysis || !this.brandAnalysis.analyzed_at) {
        return null
      }

      return this.formatBrandDate(this.brandAnalysis.analyzed_at)
    },
    brandLogoCount() {
      return this.brandAnalysis && this.brandAnalysis.logos
        ? this.brandAnalysis.logos.length
        : 0
    },
    detectedBrandColors() {
      return this.detectedColors()
    },
    brandColorCount() {
      return this.detectedBrandColors.length
    },
    brandCssVariables() {
      if (!this.brandAnalysis || !this.brandAnalysis.colors || !this.brandAnalysis.colors.css_variables) {
        return []
      }

      return this.brandAnalysis.colors.css_variables
    },
    brandFontCount() {
      return this.brandAnalysis && this.brandAnalysis.fonts
        ? this.brandAnalysis.fonts.length
        : 0
    },
    brandWarningCount() {
      return this.brandAnalysis && this.brandAnalysis.warnings
        ? this.brandAnalysis.warnings.length
        : 0
    },
    brandWarningsPreview() {
      if (!this.brandAnalysis || !this.brandAnalysis.warnings) {
        return []
      }

      return this.brandAnalysis.warnings.slice(0, 4)
    },
    groupedBrandColorFields() {
      return this.brandColorFieldGroups
        .map(group => ({
          key: group.key,
          label: group.label,
          fields: this.brandColorFields.filter(field => field.group === group.key),
        }))
        .filter(group => group.fields.length)
    },
    hasDetectedBrandOptions() {
      return this.brandLogoOptions.length > 1 || this.brandColorOptions.length > 1
    },
    hasBrandApplySelection() {
      return this.hasSelectedValues(this.brandImageSelection) || this.hasSelectedValues(this.brandColorSelection)
    },
    selectedBrandImageFields() {
      return this.brandImageFields
        .filter(field => this.brandImageSelection[field.key])
        .map(field => ({
          key: field.key,
          label: field.label,
          value: this.brandImageSelection[field.key],
        }))
    },
    selectedBrandColorFields() {
      return this.brandColorFields
        .filter(field => this.brandColorSelection[field.key])
        .map(field => ({
          key: field.key,
          label: field.label,
          value: this.brandColorSelection[field.key],
        }))
    },
    selectedBrandFieldCount() {
      return this.selectedBrandImageFields.length + this.selectedBrandColorFields.length
    },
    rateGoodForecast() {
      return Math.round((this.goodForecastCount / ((this.goodForecastCount + this.badForecastCount) === 0 ? 1 : (this.goodForecastCount + this.badForecastCount))) * 100)
    },
    goodForecastCount() {
      return (this.platform.good_forecasts_count + this.platform.good_forecasts_quiz_count)
    },
    badForecastCount() {
      return this.platform.bad_forecasts_count + this.platform.bad_forecasts_quiz_count
    },
  },
  created() {
    this.apiUrl = `platforms/${this.$route.params.id}/users`
  },
  mounted() {
    this.fetchPlatform()
  },
  beforeDestroy() {
    this.$store.commit('app/UPDATE_PAGE_META', { pageTitle: '', breadcrumb: [] })
  },
  methods: {
    async fetchPlatform() {
      try {
        this.loading = true
        await this.$store.dispatch('platforms/fetchPlatform', this.$route.params.id)
        this.hydrateBrandAnalysisFromPlatform()
        this.$store.commit('app/UPDATE_PAGE_META', {
          pageTitle: this.platform.name,
          breadcrumb: [
            {
              text: 'Plateformes',
              active: false,
              to: {
                name: 'platforms',
              },
            },
            {
              text: this.platform.name,
              active: true,
            },
          ],
        })
        this.loading = false
      } catch (err) {
        this.loading = false
        this.$toast(
          {
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Erreur',
              icon: 'AlertCircleIcon',
              variant: 'danger',
              text: `Une erreur s'est produite. erreur: ${err}`,
            },
          },
          { timeout: 5000 },
        )
      }
    },
    async deletePlatform() {
      try {
        this.deleteLoading = true
        await axiosIns.delete(`platforms/${this.platform.uuid}`)
        this.$toast(
          {
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Enregistré !',
              icon: 'CheckCircleIcon',
              variant: 'success',
              text: 'La platforme a été supprimée',
            },
          },
          { timeout: 5000 },
        )
        this.deleteLoading = false
        this.hideModal()
        await this.$router.push({
          path: '/platforms',
        })
      } catch (err) {
        this.$toast(
          {
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Erreur',
              icon: 'AlertCircleIcon',
              variant: 'danger',
              text: `Une erreur s'est produite. erreur: ${err}`,
            },
          },
          { timeout: 5000 },
        )
        this.deleteLoading = false
      }
    },
    async analyzeBrand(forceRefresh = false) {
      try {
        this.brandAnalysisLoading = true
        this.brandAnalysisError = null
        this.brandAnalysis = null
        this.brandAnalysisCached = false
        this.resetBrandSelections()
        const response = await axiosIns.post(`platforms/${this.platform.uuid}/analyzeBrand`, {
          force_refresh: forceRefresh,
        })
        this.brandAnalysis = response.data.data
        this.brandAnalysisCached = !!(response.data.meta && response.data.meta.cached)
        this.prepareBrandSelections()
        this.$toast(
          {
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Analyse terminee',
              icon: 'CheckCircleIcon',
              variant: 'success',
              text: response.data.meta && response.data.meta.cached
                ? 'La charte graphique stockee a ete chargee'
                : 'La charte graphique a ete detectee et stockee',
            },
          },
          { timeout: 5000 },
        )
      } catch (err) {
        this.brandAnalysisCached = false
        this.brandAnalysisError = err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : `Une erreur s'est produite. erreur: ${err}`
        this.$toast(
          {
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Erreur',
              icon: 'AlertCircleIcon',
              variant: 'danger',
              text: this.brandAnalysisError,
            },
          },
          { timeout: 5000 },
        )
      } finally {
        this.brandAnalysisLoading = false
      }
    },
    hydrateBrandAnalysisFromPlatform() {
      if (this.platform.client_website_brand_analysis) {
        this.brandAnalysis = this.platform.client_website_brand_analysis
        this.brandAnalysisError = null
        this.brandAnalysisCached = true
        this.prepareBrandSelections()
        return
      }

      this.brandAnalysis = null
      this.brandAnalysisError = null
      this.brandAnalysisCached = false
      this.resetBrandSelections()
    },
    showBrandApplyConfirmation() {
      if (!this.hasBrandApplySelection) {
        return
      }

      this.$refs['brand-apply-modal'].show()
    },
    hideBrandApplyConfirmation() {
      this.$refs['brand-apply-modal'].hide()
    },
    async applyBrandAnalysis() {
      try {
        this.brandApplyLoading = true
        const response = await axiosIns.post(`platforms/${this.platform.uuid}/applyBrandAnalysis`, {
          images: this.selectedValues(this.brandImageSelection),
          colors: this.selectedValues(this.brandColorSelection),
        })
        this.$store.commit('platforms/SET_PLATFORM', response.data.data)
        this.resetBrandSelections()
        this.hideBrandApplyConfirmation()
        this.$toast(
          {
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Personnalisation appliquee',
              icon: 'CheckCircleIcon',
              variant: 'success',
              text: 'Les elements selectionnes ont ete importes sur S3 et appliques a la plateforme',
            },
          },
          { timeout: 5000 },
        )
      } catch (err) {
        const message = err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : `Une erreur s'est produite. erreur: ${err}`
        this.$toast(
          {
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Erreur',
              icon: 'AlertCircleIcon',
              variant: 'danger',
              text: message,
            },
          },
          { timeout: 5000 },
        )
      } finally {
        this.brandApplyLoading = false
      }
    },
    prepareBrandSelections() {
      this.resetBrandSelections()

      const iconLogo = this.firstLogoUrl(['icon', 'manifest'])
      const mainLogo = this.firstLogoUrl(['json_ld', 'img', 'meta', 'icon', 'manifest'])
      const colors = this.detectedColors()
      const primaryColor = colors[0] || ''
      const secondaryColor = colors[1] || primaryColor

      this.brandImageSelection.favicon = iconLogo
      this.brandImageSelection.img_logo_before_auth = mainLogo
      this.brandImageSelection.img_logo_after_auth = mainLogo

      this.brandColorSelection.color_button_before_auth = primaryColor
      this.brandColorSelection.color_navigation_active_platform = primaryColor
      this.brandColorSelection.color_navigation_notification = secondaryColor
      this.brandColorSelection.color_badges_rank = secondaryColor
    },
    resetBrandSelections() {
      this.brandImageSelection = {
        favicon: '',
        img_logo_before_auth: '',
        img_logo_after_auth: '',
      }
      this.brandColorSelection = {
        color_background_before_auth: '',
        color_text_before_auth: '',
        color_button_before_auth: '',
        color_navigation_link_before_auth: '',
        color_background_after_auth: '',
        color_navigation_fixture: '',
        color_card_fixture: '',
        color_arrow_fixture: '',
        color_notification_fixture: '',
        color_navigation_platform: '',
        color_navigation_active_platform: '',
        color_navigation_background_platform: '',
        color_navigation_notification: '',
        color_badges_rank: '',
      }
    },
    firstLogoUrl(preferredTypes) {
      if (!this.brandAnalysis || !this.brandAnalysis.logos || !this.brandAnalysis.logos.length) {
        return ''
      }

      for (let index = 0; index < preferredTypes.length; index += 1) {
        const logo = this.brandAnalysis.logos.find(candidate => candidate.type === preferredTypes[index])
        if (logo && logo.url) {
          return logo.url
        }
      }

      return this.brandAnalysis.logos[0].url || ''
    },
    detectedColors() {
      const colors = []
      const seen = {}
      const pushColor = value => {
        if (!this.isHexColor(value)) {
          return
        }
        const normalizedColor = value.toUpperCase()
        if (!seen[normalizedColor]) {
          seen[normalizedColor] = true
          colors.push(normalizedColor)
        }
      }

      if (!this.brandAnalysis || !this.brandAnalysis.colors) {
        return colors
      }

      if (this.brandAnalysis.colors.suggested) {
        this.brandAnalysis.colors.suggested.forEach(pushColor)
      }

      if (this.brandAnalysis.colors.css_variables) {
        this.brandAnalysis.colors.css_variables.forEach(color => {
          pushColor(color.value)
        })
      }

      return colors
    },
    selectedValues(values) {
      const selected = {}
      Object.keys(values).forEach(key => {
        if (values[key]) {
          selected[key] = values[key]
        }
      })
      return selected
    },
    hasSelectedValues(values) {
      return Object.keys(values).some(key => !!values[key])
    },
    isHexColor(value) {
      return typeof value === 'string' && /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(value)
    },
    shortUrl(url) {
      if (!url) {
        return ''
      }

      if (url.length <= 80) {
        return url
      }
      return `${url.substring(0, 77)}...`
    },
    formatBrandDate(date) {
      const parsedDate = new Date(date)

      if (Number.isNaN(parsedDate.getTime())) {
        return null
      }

      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(parsedDate)
    },
    hideModal() {
      this.$refs[`modal-delete-platform-${this.platform.uuid}`].hide()
    },
  },
}
</script>

<style lang="scss" scoped>
.brand-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  gap: 1rem;
}

.brand-card-title {
  min-width: 0;
}

.brand-card-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.brand-loading {
  display: flex;
  align-items: center;
  min-height: 96px;
  color: #6e6b7b;
}

.brand-summary-band {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border: 1px solid #ebe9f1;
  border-radius: 4px;
  margin-bottom: 1rem;
  padding: 1rem;
  background-color: #f8f8f8;
}

.brand-summary-main {
  min-width: 0;
}

.brand-summary-title {
  overflow: hidden;
  max-width: 100%;
  margin-bottom: .25rem;
  font-size: 1rem;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.brand-summary-meta {
  display: flex;
  flex-wrap: wrap;
  gap: .5rem 1rem;
  min-width: 0;
  color: #6e6b7b;
}

.brand-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(76px, 1fr));
  gap: .5rem;
  flex: 0 0 auto;
}

.brand-metric {
  min-width: 76px;
  border: 1px solid #ebe9f1;
  border-radius: 4px;
  padding: .5rem .75rem;
  background-color: #fff;
  text-align: center;
}

.brand-metric-value {
  display: block;
  color: #5e5873;
  font-size: 1.2rem;
  font-weight: 600;
  line-height: 1.1;
}

.brand-metric-label {
  display: block;
  color: #6e6b7b;
  font-size: .75rem;
}

.brand-discovery-grid {
  display: grid;
  grid-template-columns: minmax(260px, 1.25fr) minmax(240px, 1fr) minmax(200px, .75fr);
  gap: 1rem;
}

.brand-panel {
  min-width: 0;
}

.brand-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 28px;
  margin-bottom: .75rem;
}

.brand-logo-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: .75rem;
}

.brand-logo-candidate {
  border: 1px solid #ebe9f1;
  border-radius: 4px;
  padding: .75rem;
  min-width: 0;
}

.brand-logo-candidate img {
  width: 100%;
  height: 72px;
  object-fit: contain;
  background-color: #f8f8f8;
}

.brand-logo-meta {
  display: flex;
  align-items: center;
  min-width: 0;
  margin-top: .5rem;
}

.brand-logo-label {
  overflow: hidden;
  min-width: 0;
  color: #6e6b7b;
  font-size: .85rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.brand-logo-link {
  display: block;
  overflow: hidden;
  margin-top: .35rem;
  font-size: .8rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.brand-color-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(112px, 1fr));
  gap: .5rem;
}

.brand-color-token {
  display: flex;
  align-items: center;
  min-width: 0;
  border: 1px solid #ebe9f1;
  border-radius: 4px;
  padding: .4rem .5rem;
  background-color: #fff;
  font-size: .85rem;
}

.brand-color {
  display: flex;
  align-items: center;
  margin-bottom: .5rem;
}

.brand-color-swatch {
  display: inline-block;
  width: 32px;
  height: 32px;
  border: 1px solid #d8d6de;
  border-radius: 4px;
  margin-right: .5rem;
}

.brand-color-swatch-sm {
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
}

.brand-css-vars {
  margin-top: 1rem;
}

.brand-css-var {
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr) auto;
  align-items: center;
  gap: .5rem;
  margin-bottom: .45rem;
  color: #6e6b7b;
  font-size: .8rem;
}

.brand-css-var-name {
  overflow: hidden;
  min-width: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.brand-font-list {
  line-height: 1.8;
}

.brand-apply-section {
  border-top: 1px solid #ebe9f1;
  margin-top: 1rem;
  padding-top: 1rem;
}

.brand-selected-color {
  display: flex;
  align-items: center;
  margin-top: .5rem;
  font-size: .85rem;
}

.brand-selected-image {
  display: flex;
  align-items: center;
  min-width: 0;
  margin-top: .5rem;
}

.brand-confirm-item {
  display: flex;
  align-items: center;
  min-width: 0;
  margin-bottom: .5rem;
}

.brand-color-group {
  border-top: 1px solid #ebe9f1;
  padding-top: 1rem;
}

.brand-color-group + .brand-color-group {
  margin-top: .25rem;
}

.brand-select {
  min-width: 0;
}

.brand-select-option {
  display: flex;
  align-items: center;
  min-width: 0;
}

.brand-select-option-selected {
  max-width: 100%;
}

.brand-select-option-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.brand-select-option-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.brand-select-logo-preview {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  border: 1px solid #ebe9f1;
  border-radius: 4px;
  margin-right: .5rem;
  background-color: #f8f8f8;
}

.brand-select-logo-preview img {
  max-width: 30px;
  max-height: 30px;
  object-fit: contain;
}

.brand-color-swatch-option {
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
}

.brand-color-swatch-empty {
  background: repeating-linear-gradient(
    45deg,
    #f8f8f8,
    #f8f8f8 4px,
    #ebe9f1 4px,
    #ebe9f1 8px
  );
}

@media (max-width: 991.98px) {
  .brand-card-header,
  .brand-summary-band {
    align-items: stretch;
    flex-direction: column;
  }

  .brand-card-actions {
    justify-content: flex-start;
  }

  .brand-metrics {
    width: 100%;
  }

  .brand-discovery-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 575.98px) {
  .brand-metrics {
    grid-template-columns: 1fr;
  }

  .brand-summary-title {
    white-space: normal;
  }
}

</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
